import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeInWhenVisible, SectionHeader } from './SectionWrapper';
import { PROJECTS } from '../data/portfolio';

function ProjectModal({ project, isDark, onClose }) {
  return (
    <AnimatePresence>
      {project && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/70 nav-blur"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed inset-x-4 top-[10%] z-50 max-w-2xl mx-auto rounded-2xl border p-8 overflow-y-auto max-h-[80vh] ${
              isDark
                ? 'bg-navy-700 border-navy-300/50'
                : 'bg-white border-gray-200 shadow-2xl'
            }`}
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className={`font-mono text-xs tracking-widest mb-1 ${isDark ? 'text-gold/70' : 'text-gold-muted/70'}`}>
                  @ {project.company}
                </p>
                <h3 className={`font-sans font-black text-xl tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {project.title}
                </h3>
              </div>
              <button
                onClick={onClose}
                className={`font-mono text-xl w-8 h-8 flex items-center justify-center rounded-full border bg-transparent cursor-pointer transition-colors ${
                  isDark
                    ? 'text-gray-400 border-navy-300/50 hover:text-white hover:border-gray-300'
                    : 'text-gray-400 border-gray-200 hover:text-gray-800'
                }`}
              >
                ×
              </button>
            </div>

            {/* Impact */}
            <div
              className="rounded-lg px-4 py-3 mb-6 font-mono text-sm"
              style={{
                background: 'rgba(232,184,75,0.08)',
                border: '1px solid rgba(232,184,75,0.25)',
                color: 'var(--gold-light, #f5d078)',
              }}
            >
              ↑ {project.impact}
            </div>

            {/* Description */}
            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {project.description}
            </p>

            {/* Key highlights */}
            <div className="mb-6">
              <p className={`font-mono text-xs tracking-widest mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                // key highlights
              </p>
              <ul className="space-y-2">
                {project.highlights.map((h) => (
                  <li key={h} className={`flex gap-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <span style={{ color: 'var(--gold)' }} className="font-mono mt-0.5 flex-shrink-0">→</span>
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Tech stack */}
            <div>
              <p className={`font-mono text-xs tracking-widest mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                // tech stack
              </p>
              <div className="flex flex-wrap gap-2">
                {project.stack.map((tech) => (
                  <span
                    key={tech}
                    className={`font-mono text-xs px-2.5 py-1 rounded border ${
                      isDark
                        ? 'text-gray-400 bg-navy-800/60 border-navy-400/50'
                        : 'text-gray-500 bg-gray-50 border-gray-200'
                    }`}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ProjectCard({ project, isDark, index, onClick }) {
  return (
    <FadeInWhenVisible delay={index * 0.1}>
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        onClick={onClick}
        className={`relative rounded-xl border p-7 flex flex-col h-full cursor-pointer group overflow-hidden transition-all duration-300 card-hover-glow ${
          isDark
            ? 'bg-navy-600 border-navy-300/50 hover:border-gold-dark/60'
            : 'bg-white border-gray-200 hover:border-gold-dark/40 shadow-sm'
        }`}
      >
        {/* Subtle gradient overlay on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl"
          style={{ background: 'linear-gradient(135deg, rgba(232,184,75,0.04), transparent)' }}
        />

        <div className="flex justify-between items-start mb-5">
          <span
            className="font-sans font-black leading-none"
            style={{ fontSize: '2.8rem', color: isDark ? '#2a3550' : '#e5e7eb' }}
          >
            {project.num}
          </span>
          <motion.span
            className={`text-lg transition-colors duration-200 ${isDark ? 'text-gray-600' : 'text-gray-300'} group-hover:text-gold`}
            animate={{ x: 0, y: 0 }}
            whileHover={{ x: 3, y: -3 }}
          >
            ↗
          </motion.span>
        </div>

        <p className={`font-mono text-xs tracking-widest mb-2 ${isDark ? 'text-gold/70' : 'text-gold-muted/70'}`}>
          @ {project.company}
        </p>
        <h3 className={`font-sans font-black text-lg tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {project.title}
        </h3>
        <p className={`text-sm leading-relaxed mb-5 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {project.description}
        </p>

        {/* Impact */}
        <div
          className="rounded-lg px-3.5 py-2.5 mb-5 text-xs font-mono"
          style={{
            background: 'rgba(232,184,75,0.08)',
            border: '1px solid rgba(232,184,75,0.2)',
            color: '#f5d078',
          }}
        >
          ↑ {project.impact}
        </div>

        {/* Stack */}
        <div className="flex flex-wrap gap-1.5">
          {project.stack.map((tech) => (
            <span
              key={tech}
              className={`font-mono text-[10px] px-2 py-1 rounded border ${
                isDark
                  ? 'text-gray-500 bg-navy-700/60 border-navy-400/50'
                  : 'text-gray-400 bg-gray-50 border-gray-200'
              }`}
            >
              {tech}
            </span>
          ))}
        </div>

        <p className={`font-mono text-xs mt-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
          click to view details →
        </p>
      </motion.div>
    </FadeInWhenVisible>
  );
}

export default function Projects({ isDark }) {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id="projects"
      className={`py-24 ${isDark ? 'bg-navy-800' : 'bg-gray-50'}`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeader tag="// 03. featured work" heading="Projects &amp; Impact" isDark={isDark} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.map((project, i) => (
            <ProjectCard
              key={project.num}
              project={project}
              isDark={isDark}
              index={i}
              onClick={() => setSelected(project)}
            />
          ))}
        </div>
      </div>

      <ProjectModal
        project={selected}
        isDark={isDark}
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
